import type {
  SponsorCategoryDto,
  SponsorFurtherRequests,
  SponsoringOfferDto,
  SponsorRequestDto,
  SponsorRequestStatus,
} from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { Badge, cn } from '../ui';

export const FURTHER_REQUEST_LABELS: Record<SponsorFurtherRequests, string> = {
  OPEN: 'Anfragen möglich',
  VIA_MANAGER: 'Nur über Betreuung',
  NO: 'Bitte nicht anfragen',
};

export const FURTHER_REQUEST_ICONS: Record<SponsorFurtherRequests, string> = {
  OPEN: '🟢',
  VIA_MANAGER: '🟡',
  NO: '🔴',
};

export const FURTHER_REQUEST_TONES = { OPEN: 'success', VIA_MANAGER: 'warning', NO: 'danger' } as const;

export const REQUEST_STATUS_LABELS: Record<SponsorRequestStatus, string> = {
  PLANNED: 'Geplant',
  REQUESTED: 'Angefragt',
  ACCEPTED: 'Zugesagt',
  DECLINED: 'Abgesagt',
  CANCELLED: 'Zurückgezogen',
};

export const REQUEST_STATUS_TONES = {
  PLANNED: 'neutral',
  REQUESTED: 'info',
  ACCEPTED: 'success',
  DECLINED: 'danger',
  CANCELLED: 'neutral',
} as const;

export function FurtherRequestsBadge({ value }: { value: SponsorFurtherRequests }): JSX.Element {
  return (
    <Badge tone={FURTHER_REQUEST_TONES[value]}>
      <span aria-hidden>{FURTHER_REQUEST_ICONS[value]}</span> {FURTHER_REQUEST_LABELS[value]}
    </Badge>
  );
}

export function RequestStatusBadge({ status }: { status: SponsorRequestStatus }): JSX.Element {
  return <Badge tone={REQUEST_STATUS_TONES[status]}>{REQUEST_STATUS_LABELS[status]}</Badge>;
}

/** Kurzform einer Anfrage: "12.08.2026 · Lukas Steiner · Dressen U12". */
export function requestSummary(request: SponsorRequestDto): string {
  return [
    formatDate(request.requestedAt ?? request.createdAt),
    request.requestedBy?.name,
    request.offerTitle ?? request.topic,
  ].filter(Boolean).join(' · ');
}

/** Logo oder Initialen als Platzhalter. */
export function SponsorLogo({
  name,
  url,
  size = 'md',
}: {
  name: string;
  url: string | null;
  size?: 'sm' | 'md' | 'lg';
}): JSX.Element {
  const box = { sm: 'h-10 w-10 text-sm', md: 'h-14 w-14 text-base', lg: 'h-24 w-24 text-2xl' }[size];
  if (url) {
    return (
      <img
        src={url}
        alt={`Logo ${name}`}
        className={cn(box, 'shrink-0 rounded-lg border border-slate-200 bg-white object-contain p-1')}
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .filter((part) => /^[\p{L}\p{N}]/u.test(part))
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
  return (
    <span
      aria-hidden
      className={cn(box, 'flex shrink-0 items-center justify-center rounded-lg bg-slate-100 font-semibold text-slate-500')}
    >
      {initials || '?'}
    </span>
  );
}

export function useSponsorCategories(includeArchived = false) {
  return useQuery({
    queryKey: ['sponsor-categories'],
    queryFn: () => api.get<SponsorCategoryDto[]>('/api/sponsor-categories'),
    select: (list) => (includeArchived ? list : list.filter((c) => !c.archived)),
  });
}

export function useSponsoringOffers(includeArchived = false) {
  return useQuery({
    queryKey: ['sponsoring-offers', includeArchived],
    queryFn: () => api.get<SponsoringOfferDto[]>('/api/sponsoring-offers', includeArchived ? { includeArchived: true } : {}),
  });
}

/** Beschreibt die Belegung einer Werbemoeglichkeit. */
export function OfferAvailability({ offer }: { offer: SponsoringOfferDto }): JSX.Element {
  const tone = offer.free === 0 ? 'danger' : offer.free < offer.capacity ? 'warning' : 'success';
  return (
    <Badge tone={tone}>
      {offer.capacity === 1
        ? offer.free === 0 ? 'Vergeben' : 'Frei'
        : `${offer.free} von ${offer.capacity} frei`}
    </Badge>
  );
}
